<template>
  <div
    ref="triggerRef"
    class="sk-select__container"
   data-testid="components-ui-sk-select-sk-select-div">
    <div 
      :class="skSelectClass"
      @click="toggleDropdown"
     data-testid="components-ui-sk-select-sk-select-div-2">
      <div :class="skSelectWrapperClass" data-testid="components-ui-sk-select-sk-select-div-3">
        <div
          v-if="label && isLargeSize"
          class="sk-select__label"
         data-testid="components-ui-sk-select-sk-select-div-4">
          {{ label }}:
        </div>

        <div
          class="sk-select__inner"
         data-testid="components-ui-sk-select-sk-select-div-5">
          <template v-if="!isEmptySelectedValue">
            <template v-if="multiple && chips">
              <div
                v-for="(item, index) in chipLayout.chips"
                :key="getItemKey(item, index)"
                class="sk-select__chip bg-white py-[5.5px] pl-[2px] pr-[10px] rounded-[6px] flex items-center gap-x-[2px] max-w-[300px] min-w-0 shrink"
               data-testid="components-ui-sk-select-sk-select-div-6">
                <div
                  class="hover:opacity-50 transition-opacity duration-300"
                  @click.stop="deleteItem(item)"
                 data-testid="components-ui-sk-select-sk-select-div-7">
                  <SkCloseChipIcon data-testid="components-ui-sk-select-sk-select-sk-close-chip-icon" />
                </div>
                <span class="text-[#292929] text-xs font-semibold font-['Source Sans Pro'] whitespace-nowrap overflow-hidden text-ellipsis min-w-0" data-testid="components-ui-sk-select-sk-select-span">
                  {{ itemValue ? item[itemTitle] : item }}
                </span>
              </div>

              <div
                v-if="chipLayout.total > chipLayout.chips.length"
                class="sk-select__chip-counter shrink-0 font-semibold font-['Source Sans Pro'] text-xs bg-[#5C9DEB] rounded-[30px] text-[#F6F7FA] px-[10px] py-[5.5px] whitespace-nowrap"
               data-testid="components-ui-sk-select-sk-select-div-8">
                {{ $t('ui.select.total') }}: {{ chipLayout.total }}
              </div>
            </template>
            <template v-else>
              <span class="whitespace-nowrap text-ellipsis" data-testid="components-ui-sk-select-sk-select-span-2">{{ valueText }}</span>
            </template>
          </template>

          <template v-else>
            <div class="sk-select__inner--placeholder" data-testid="components-ui-sk-select-sk-select-div-9">
              {{ placeholder || $t('fill_field') }}
            </div>
          </template>
        </div>
      </div>

      <div class="sk-select__icon" data-testid="components-ui-sk-select-sk-select-div-10">
        <div v-if="isNotSmallSize" data-testid="components-ui-sk-select-sk-select-div-11">
          <slot name="append-icon" />
        </div>
        
        <div 
          v-if="!disabled && !readonly" 
          class="sk-select__icon--arrow transform" 
          :class="{ 'rotate-180': isOpen }"
         data-testid="components-ui-sk-select-sk-select-div-12">
          <SkArrowUpIcon data-testid="components-ui-sk-select-sk-select-sk-arrow-up-icon" />
        </div>
      </div>

      <teleport to="body">
        <div
          v-if="isOpen && !disabled && !readonly" 
          v-click-outside="handleClickOutside"
          class="sk-select__dropdown--wrapper"
          :style="{
            position: 'fixed',
            top: '0px',
            left: '0px',
            width: position.width + 'px',
            transform: `translate(${position.left}px, ${position.top}px)`
          }"
         data-testid="components-ui-sk-select-sk-select-div-13">
          <div           
            ref="dropdownContainerRef"   
            class="sk-select__dropdown transition-transform duration-200"
            :class="position.name === 'top' ? 'sk-select__animation--top': 'sk-select__animation--bottom'"
           data-testid="components-ui-sk-select-sk-select-div-14">
            <div
              v-if="search"
              class="sk-select__dropdown--search m-2 h-6 flex items-center gap-x-2 py-1 px-[10px] bg-white rounded-[6px]"
              @click.stop
             data-testid="components-ui-sk-select-sk-select-div-15">
              <SkSearchIcon class="flex-shrink-0" data-testid="components-ui-sk-select-sk-select-sk-search-icon" />

              <SkTextSelect
                v-if="searchItems.length > 0"
                v-model="activeMenuItem"
                item-title="name"
                item-value="value"
                :items="searchItems"
               data-testid="components-ui-sk-select-sk-select-sk-text-select">
                <template #activator="{ toggle, title, isOpened }">
                  <button
                    class="h-[19px] outline-none text-xs text-[#3A7CCA] font-bold whitespace-nowrap px-[6px] rounded-[3px] transition-colors duration-300"
                    :class="{ 'bg-[#DBEBFF]': isOpened }"
                    @click.stop="toggle"
                   data-testid="components-ui-sk-select-sk-select-button">
                    <div class="flex items-center gap-x-[6px]" data-testid="components-ui-sk-select-sk-select-div-16">
                      {{ title }}

                      <div 
                        class="sk-select__icon--arrow-menu transform" 
                        :class="{ 'rotate-180': isOpened }"
                       data-testid="components-ui-sk-select-sk-select-div-17">
                        <SkArrowUpIcon data-testid="components-ui-sk-select-sk-select-sk-arrow-up-icon-2" />
                      </div>
                    </div>
                  </button>
                </template>
              </SkTextSelect>

              <input
                ref="dropdownSearchInputRef"
                v-model="stringToFilter"
                class="outline-none w-full"
                type="text"
                :placeholder="searchPlaceholder || $t('ui.select.search.placeholder')"
                @input="handleSearch"
                @click.stop
               data-testid="components-ui-sk-select-sk-select-input">
            </div>

            <div
              v-if="selectOptions"
              class="px-3 py-[6px] flex justify-between items-center border-y border-solid border-[#DCE1E7] cursor-default"
              @click.stop
             data-testid="components-ui-sk-select-sk-select-div-18">
              <div class="text-[#171717] text-xs leading-[140%]" data-testid="components-ui-sk-select-sk-select-div-19">
                {{ $t('ui.select.selected') }}: {{ selectedValue.length }}
              </div>
              <template v-if="!selectedValue.length">
                <button
                  class="outline-none text-[#3A7CCA] text-xs leading-[140%]"
                  @click.stop="selectAll"
                 data-testid="components-ui-sk-select-sk-select-button-2">
                  {{ $t('ui.select.select-all') }}
                </button>
              </template>

              <template v-else>
                <button
                  class="outline-none text-[#D45050] text-xs leading-[140%]"
                  @click.stop="cancelAll"
                 data-testid="components-ui-sk-select-sk-select-button-3">
                  {{ $t('ui.select.cancel-all') }}
                </button>
              </template>
            </div>

            <div
              ref="dropdownRef"
              class="sk-select__dropdown--container"
              @scroll="onScroll"
             data-testid="components-ui-sk-select-sk-select-div-20">
              <div
                :style="{ height: `${dropdownContentHeight}px` }"
                class="relative"
               data-testid="components-ui-sk-select-sk-select-div-21">
                <template v-if="isDropdownLoading">
                  <div class="absolute h-full w-full flex items-center justify-center" data-testid="components-ui-sk-select-sk-select-div-22">
                    <SpinnerIcon
                      class="animate-spin"
                    data-testid="components-ui-sk-select-sk-select-spinner-icon" />
                  </div>
                </template>
                <template v-else>
                  <template v-if="visibleItems.length > 0">
                    <div
                      v-for="(item, index) in visibleItems"  
                      :key="getItemKey(item, startIndex + index)"
                      class="sk-select__dropdown--item"
                      :style="{
                        top: (startIndex + index) * 36 + 'px',
                      }"
                      :class="{ 'sk-select__dropdown--item-active': isActiveItem(item) }"
                      @click.stop="selectItem(item)"
                     data-testid="components-ui-sk-select-sk-select-div-23">
                      <template v-if="multiple">
                        <SkCheckbox
                          class="pointer-events-none"
                          :checked="isSelected(item)"
                          :style="{ background: 'transparent' }"
                        data-testid="components-ui-sk-select-sk-select-sk-checkbox" />

                        <span data-testid="components-ui-sk-select-sk-select-span-3">
                          {{ itemValue ? item[itemTitle] : item }}
                        </span>
                      </template>
                      <template v-else>
                        <span data-testid="components-ui-sk-select-sk-select-span-4">
                          {{ itemValue ? item[itemTitle] : item }}
                        </span>
                      </template>
                    </div>
                  </template>

                  <template v-else>
                    <div class="absolute h-full w-full flex items-center justify-center text-xs text-[#A6AEB8]" data-testid="components-ui-sk-select-sk-select-div-24">
                      {{ $t('ui.select.noData') }}
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
      </teleport>
    </div>

    <Transition
      name="sk-hint-slide-fade"
     data-testid="components-ui-sk-select-sk-select-transition">
      <div
        v-if="errorState"
        class="sk-select--error-hint"
       data-testid="components-ui-sk-select-sk-select-div-25">
        {{ errorText }}
      </div>
    </Transition>

    <div
      v-if="hint"
      class="sk-select__hint"
     data-testid="components-ui-sk-select-sk-select-div-26">
      {{ hint }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount } from 'vue'

import SkArrowUpIcon from '@/components/ui/SkSelect/assets/ArrowUp.vue'
import SkCheckbox from '@/components/ui/checkbox.vue'
import SkSearchIcon from '@/components/icons/SkSearchIcon.vue'
import SkCloseChipIcon from './assets/SkCloseChipIcon.vue'
import SpinnerIcon from "@/components/ui/Button/SpinnerIcon.vue"

import debounce from 'lodash/debounce'
import SkTextSelect from './SkTextSelect.vue'

const emits = defineEmits(['update:modelValue', 'close', 'reach-end', 'select-all'])

const props = defineProps({
  modelValue: {
    type: [String, Object, Array],
    default: () => '',
  },
  size: {
    type: String,
    default: 'lg',
    validator(value) {
      return ['sm', 'md', 'lg'].includes(value)
    }
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false
  },
  maxWidth: {
    type: String,
    default: "100%",
  },
  hint: {
    type: String,
    default: ''
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorText: {
    type: String,
    default: "",
  },
  items: {
    type: Array,
    default: () => []
  },
  itemTitle: {
    type: String,
    default: ''
  },
  itemValue: {
    type: String,
    default: ''
  },
  multiple: {
    type: Boolean,
    default: false
  },
  maxHeight: {
    type: Number,
    default: 328
  },
  search: {
    type: Boolean,
    default: false
  },
  searchKey: {
    type: String,
    default: ''
  },
  searchItems: {
    type: Array,
    default: () => []
  },
  searchFunction: {
    type: Function,
    default: null
  },
  searchPlaceholder: {
    type: String,
    default: ''
  },
  selectOptions: {
    type: Boolean,
    default: false
  },
  chips: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  onSelectAllCallback: {
    type: Function,
    default: null
  }
})

const isOpen = ref(false)
const triggerRef = ref(null)
const dropdownContainerRef = ref(null)
const dropdownRef = ref(null)
const startIndex = ref(0)
const stringToFilter = ref('')
const selectContainerWidth = ref(0)
const activeMenuItem = ref(props.searchItems[0])
const position = ref({ top: 0, left: 0, width: 0 });
const dropdownSearchInputRef = ref(null)
const selectAllLoading = ref(false)
let mutationObserver = null

const selectedValue = computed({
  get: () => props.modelValue,
  set: (val) => emits('update:modelValue', val)
})
const selectedList = computed(() => Array.isArray(selectedValue.value) ? selectedValue.value : [])
const getItemId = (item) => (props.itemValue ? item[props.itemValue] : item)
const selectedKeys = computed(() => new Set(selectedList.value.map(getItemId)))
const isSelected = (item) => selectedKeys.value.has(getItemId(item))

const listItems = computed(() => {
  if (!props.multiple || !isOpen.value || !selectedList.value.length) return props.items
  const unselected = props.items.filter((item) => !selectedKeys.value.has(getItemId(item)))
  return unselected.length ? [...selectedList.value, ...unselected] : props.items
})

const filteredItems = computed(() => {
  if(props.search && props.searchKey && stringToFilter.value.trim()) {
    
    
    if(props.searchItems.length > 0) {
      const searchEntity = Object.keys(props.searchItems).length > 0 ? activeMenuItem.value : activeMenuItem
      const searchKey  = searchEntity.value
      
      return listItems.value.filter((item) => item[searchKey].toString().toLowerCase().includes(stringToFilter.value.toLowerCase()))
    }
    return listItems.value.filter((item) => item[props.searchKey].toString().toLowerCase().includes(stringToFilter.value.toLowerCase()))
  } 

  return props.itemTitle ? listItems.value.filter((item) => item[props.itemTitle]?.toString()?.toLowerCase()?.includes(stringToFilter.value.toLowerCase())) : listItems.value
})

const chipLayout = computed(() => {
  const selected = selectedList.value
  if (!selected.length) return { chips: [], total: 0 }

  const available = selectContainerWidth.value
  if (!available) return { chips: selected.slice(0, 1), total: selected.length }

  const widths = selected.map((item) => {
    const title = String(props.itemTitle ? item[props.itemTitle] : item ?? '')
    return Math.min(title.length * 7, 266) + 46
  })
  const needCounter = widths.reduce((sum, width) => sum + width, 0) > available
  let remaining = available - (needCounter ? 90 : 0)
  let count = 0

  for (const width of widths) {
    if (width <= remaining) {
      remaining -= width
      count++
    } else {
      break
    }
  }

  return { chips: selected.slice(0, Math.max(count, 1)), total: selected.length }
})

const valueText = computed(() => {
  if(props.multiple) {
    if(props.itemValue) {
      return selectedValue.value.map((item) => item[props.itemTitle]).join(', ')
    } else {
      return selectedValue.value.join(', ')
    }
  } else {
    if(props.itemValue) {
      return selectedValue.value[props.itemTitle]
    } else {
      return selectedValue.value
    }
  }
})

const visibleCount = Math.ceil(props.maxHeight / 36)

const visibleItems = computed(() => {
  return filteredItems.value.slice(startIndex.value,  startIndex.value + visibleCount)
})

const isLargeSize = computed(() => props.size === 'lg')

const isNotSmallSize = computed(() => props.size !== 'sm')

const errorState = computed(() => props.error && props.errorText)

const isDropdownLoading = computed(() => props.loading || selectAllLoading.value)

const skSelectClass = computed(() => {
  return {
    'sk-select': true,
    [`sk-select-${props.size}`]: true,
    'sk-select--readonly': props.readonly,
    'sk-select--disabled': props.disabled,
    'sk-select--error': props.error
  }
})

const skSelectWrapperClass = computed(() => {
  return `sk-select__wrapper sk-select__wrapper-${props.size}`
})

// const skSelectDropdownStyle = computed(() => {
//   return {
//     position: 'absolute',
//     top: `${dropdownPosition.top}px`,
//     left: `${dropdownPosition.left}px`,
//     width: `${dropdownPosition.width}px`,
//     zIndex: 2000
//   }
// })

const totalHeight = computed(() => filteredItems.value.length * 36)

const dropdownContentHeight = computed(() => {
  if (isDropdownLoading.value) return Math.min(props.maxHeight, totalHeight.value || props.maxHeight)
  return visibleItems.value.length > 0 ? totalHeight.value : 32
})

const isEmptySelectedValue = computed(() => {
  if(!selectedValue.value) return true

  if(typeof selectedValue.value === 'string') return !selectedValue.value.trim()

  if(Array.isArray(selectedValue.value)) return !selectedValue.value.length

  if(typeof selectedValue.value === 'object') return !Object.keys(selectedValue.value).length

  return false
})

// const skSelectDropdownStyle = computed(() => {
//   const dropdownSizes = {
//     'sm': 24,
//     'md': 36,
//     'lg': 48
//   }

//   return { top: `${dropdownSizes[props.size]}px` }
// })

watch(isOpen, (val) => {
  stringToFilter.value = ''
  if (!val) return emits('close')
  nextTick(() => {
    updatePosition()
    props.search && dropdownSearchInputRef.value?.focus()
  })
})

onMounted(() => {
  if(props.multiple && props.chips) {
    const resizeObserver = new ResizeObserver(entries => {
      for(let entry of entries) {
        selectContainerWidth.value = entry.contentRect.width - 28
      }
    })

    const selectInnerEl = triggerRef.value.querySelector('.sk-select__inner')

    if(triggerRef.value) resizeObserver.observe(selectInnerEl)
  }

  const root = document.body;
  mutationObserver = new MutationObserver(handleDropdownPosition);
  mutationObserver.observe(root, { childList: true, subtree: true });

  window.addEventListener("scroll", handleDropdownPosition, true);
  window.addEventListener("resize", handleDropdownPosition);
})

onBeforeUnmount(() => {
  mutationObserver.disconnect();

  window.removeEventListener("scroll", handleDropdownPosition, true);
  window.removeEventListener("resize", handleDropdownPosition);
});

function handleDropdownPosition() {
  if (isOpen.value) updatePosition();
}

const toggleDropdown = () => {
  if(isOpen.value) {
    dropdownRef.value.scrollTop = 0
    startIndex.value = 0
  }
  isOpen.value = !isOpen.value
}

const closeDropdown = () => {
  isOpen.value = false

  dropdownRef.value.scrollTop = 0
  startIndex.value = 0
}

const selectItem = (item) => {
  if(props.multiple) {
    if(isSelected(item)) {
      if(props.itemValue) {
        selectedValue.value = selectedValue.value.filter((v) => {
          return v[props.itemValue] !== item[props.itemValue]
        })    
      } else {
        selectedValue.value = selectedValue.value.filter((v) => v !== item)
      }
    } else {
      selectedValue.value = [...selectedValue.value, item]
    }
  } else {
    selectedValue.value = item

    closeDropdown()
  }
}

const getSelectAllItems = () => (
  stringToFilter.value.trim() ? filteredItems.value : props.items
)

const selectAll = async () => {
  if (!props.multiple || selectAllLoading.value) return

  const itemsToSelect = getSelectAllItems()

  if (itemsToSelect.length < 100 && !props.onSelectAllCallback) {
    selectedValue.value = [...itemsToSelect]
    return
  }

  if (props.onSelectAllCallback) {
    selectAllLoading.value = true

    try {
      const allItems = await props.onSelectAllCallback()
      if (allItems && Array.isArray(allItems)) {
        selectedValue.value = allItems
        return
      }
    } finally {
      selectAllLoading.value = false
    }
  }

  selectedValue.value = [...itemsToSelect]
}

const cancelAll = () => {
  if(props.multiple) {
    selectedValue.value = []
  }
}

const isActiveItem = (item) => {
  if (props.multiple) return isSelected(item)
  if (!selectedValue.value) return false
  return props.itemValue
    ? item[props.itemValue] === selectedValue.value[props.itemValue]
    : item === selectedValue.value
}

const updatePosition = () => {
  if (!triggerRef.value || !dropdownContainerRef.value) return

  const triggerEl = triggerRef.value
  const triggerRect = triggerEl.querySelector('.sk-select').getBoundingClientRect()

  const dropdownRect = dropdownContainerRef.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight

  // Проверяем место снизу и сверху
  const middleSpace = viewportHeight / 2

  if (triggerRect.top > middleSpace) {
    position.value = {
      name: 'top',
      top: triggerRect.top + document.body.scrollTop - dropdownRect.height - 4,
      left: window.scrollX + triggerRect.left,
      width: triggerRect.width
    }
  } else {
    position.value = {
      name: 'bottom',
      top: triggerRect.top + document.body.scrollTop + triggerRect.height + 4,
      left: window.scrollX + triggerRect.left,
      width: triggerRect.width
    }
  }
}

const handleClickOutside = (event) => {
  if (triggerRef.value && !triggerRef.value.contains(event.target) ||
    dropdownContainerRef.value && !dropdownContainerRef.value.contains(event.target)) {
    closeDropdown()
  }
}

const onScroll = () => {
  if(dropdownRef.value) {
    const scrollTop = dropdownRef.value.scrollTop
    startIndex.value = Math.floor(scrollTop / 36)

    const { clientHeight, scrollHeight } = dropdownRef.value
    const isReachedEnd = scrollTop + clientHeight >= scrollHeight - 8

    if (isReachedEnd) {
      emits('reach-end')
    }
  }
}

const handleAsyncSearch = debounce((val, searchField) => {
  props.searchFunction(val, searchField)
}, 300)

const handleSearch = (event) => {
  if(!props.searchFunction) {
    stringToFilter.value = event.target.value
  } else {
    const val = event.target.value
    const searchField = props.searchItems.length > 0
      ? activeMenuItem.value?.value
      : props.searchKey

    if (!val.trim()) {
      handleAsyncSearch.cancel()
      props.searchFunction(val, searchField)
    } else {
      handleAsyncSearch(val, searchField)
    }
  }
}

const getItemKey = (item, index) => String(getItemId(item) ?? index)

const deleteItem = (item) => {
  if(props.itemValue) {
    selectedValue.value = selectedValue.value.filter((v) => v[props.itemValue] !== item[props.itemValue])
  } else {
    selectedValue.value = selectedValue.value.filter((v) => v !== item)
  }
}
</script>

<style lang="scss" scoped>
.sk-select {
  outline: none;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: 'Source Sans Pro';
  background-color: #EDEFF3;
  width: 100%;
  max-width: v-bind(maxWidth);
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  position: relative;
  
  &:hover {
    border-color: #DCE1E7;
  }

  &__container {
    width: 100%;
    max-width: v-bind(maxWidth);
  }

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &-sm {
      padding: 4px 8px 4px 8px;
    }

    &-md {
      padding: 5px 9px 5px 8px;
    }

    &-lg {
      padding: 0 9px 0px 5px;
    }
  }

  &-sm {
    min-height: 24px;
    border: 1px solid #DCE1E7;
    background-color: #FFFFFF;
    border-radius: 4px;
    font-size: 14px;
    line-height: 100%; 
    
    &:hover {
      border: 1px solid #CDD4DD;
    }
  }

  &-md {
    min-height: 36px;
    padding: 4px;
    border-radius: 10px;
  }

  &-lg {
    min-height: 48px;
    padding: 6px;
    border-radius: 12px;
    font-size: 16px;
    line-height: 19px;
  }

  &-md, &-lg {
    font-size: 16px;
    line-height: 19px;
  }

  &__label {
    font-size: 13px;
    white-space: nowrap;
    color: #292929;
    font-weight: 700;
    line-height: 16px;
  }

  &__inner {
    //height: 19px;
    font-weight: 400;
    color: #292929;
    display: flex;
    //flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-width: 0;
    overflow: hidden;

    &--placeholder {
      color: #A6AEB8;
      font-size: 16px;
      line-height: 19px;
      font-family: 'Source Sans Pro';
    }
  }

  &__chip-counter {
    flex-shrink: 0;
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 4px;
    gap: 7px;

    &--arrow {
      display: flex;
      margin-right: 5px;
      justify-content: center;
      align-items: center;
      transition: transform 0.2s ease-out;
      width: 100%;

      &-menu {
        transition: transform 0.2s ease-out;

        :deep(path) {
          stroke: #3A7CCA;
        }
      }
    }
  }

  &__hint {
    padding-left: 12px;
    margin-top: 3px;
    font-weight: 400;
    font-size: 12px;
    color: #828D9B;
    font-family: 'Source Sans Pro';
  }

  &--disabled {
    cursor: default;
    background-color: #F6F7FA;
    pointer-events: none;

    &:hover {
      border-color: transparent;
    }

    .sk-select {
      &__inner {
        color: #828D9B;
      }

      &__label {
        color: #828D9B;
      }
    }
  }

  &--readonly {
    cursor: default;
    pointer-events: none;

    &:hover {
      border-color: transparent;

      &.sk-select-sm {
        border-color: #DCE1E7;
      }
    }
  }

  &--error {
    .sk-select {
      &__label {
        color: #c93131;
      }

      &__inner {
        color: #C93131;
      }
    }

    &.sk-select-sm {
      border-color: #C93131;

      .sk-select {
        &__inner {
          color: #595959;
        }
      }
    }

    &-hint {
      padding-left: 12px;
      margin-top: 2px;
      font-weight: 400;
      font-size: 12px;
      line-height: 140%;
      color: #c93131;
    }
  }

  &__dropdown {
    display: flex;
    flex-direction: column;
    max-height: 328px;
    box-shadow: 0px 2px 8px 0px #00000014;
    background-color: #EDEFF3;
    border-radius: 12px;
    border: 1px solid #DCE1E7;
    overflow: hidden;

    &--wrapper {
      width: 100%;
      position: absolute;
      left: 0;
      z-index: 2000;
    }

    &--search {
      input {
        font-size: 14px;
        font-weight: 400;
        font-family: 'Source Sans Pro';
        color: #292929;

        &::placeholder {
          color: #828D9B;
        }
      }
    }

    &--container {
      overflow-y: auto;
      scrollbar-color: #A6AEB8 #EDEFF3;
      scrollbar-width: thin;
    }

    &--item {
      position: absolute;
      width: 100%;
      display: flex;
      gap: 6px;
      align-items: center;
      height: 36px;
      transition: background-color 0.2s ease-out;
      font-family: 'Source Sans Pro';
      font-size: 14px;
      line-height: 140%;
      color: #292929;
      padding: 5px 12px;
      cursor: pointer;

      & > span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        display: block;
      }

      &:hover {
        background-color: #F6F7FA;
      }

      &-active {
        color: #3A7CCA;

        &:hover {
          background-color: transparent;
          color: #3A7CCA;
        }
      }
    }
  }

  &__animation {
    &--top {
      animation-name: dropdown-anim-top;
      animation-duration: 0.2s;
      animation-timing-function: ease-in-out;
    }

    &--bottom {
      animation-name: dropdown-anim-bottom;
      animation-duration: 0.2s;
      animation-timing-function: ease-in-out;
    }
  }
}

.sk-hint-slide-fade-enter-active,
.sk-hint-slide-fade-leave-active {
  transition: all 0.2s ease;
}
.sk-hint-slide-fade-enter-from,
.sk-hint-slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.sk-hint-slide-fade-enter-to,
.sk-hint-slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.sk-dropdown-bottom-enter-active,
.sk-dropdown-bottom-leave-active,
.sk-dropdown-top-enter-active,
.sk-dropdown-top-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.sk-dropdown-bottom-enter-from,
.sk-dropdown-bottom-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.sk-dropdown-top-enter-from,
.sk-dropdown-top-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

@keyframes dropdown-anim-top {
  from {
    transform: translateY(5px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes dropdown-anim-bottom {
  from {
    transform: translateY(-5px); 
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>